import { gql } from '@urql/core';
import React, { useMemo } from 'react';
import Skeleton from 'react-loading-skeleton';
import { toast } from 'react-toastify';
import { AppStateUser } from '../../../client/providers/AppProvider';
import { useAuthenticatedMutation, useAuthenticatedQuery } from '../../../client/hooks';
import {
    DeleteMyPlayerGameDocument,
    DeleteMyPlayerGameMutation,
    DeleteMyPlayerGameMutationVariables,
    DeleteMyPlayerGameAndChangePrimaryGameMutation,
    DeleteMyPlayerGameAndChangePrimaryGameMutationVariables,
    DeleteMyPlayerGameAndChangePrimaryGameDocument,
    EditableGameFragment,
    PlayerMyGamesDocument,
    PlayerMyGamesQuery,
    PlayerMyGamesQueryVariables,
} from '../../../generated/spin-graphql';
import { SpinAppModals, useModals } from '../../common/SpinModalProvider';
import { EditableGame } from './EditableGame';

type Props = {
    user: AppStateUser;
};
gql`
    query PlayerMyGames($id: String!) {
        espin_user_by_pk(id: $id) {
            player_profile {
                player_games(order_by: { primary: desc }) {
                    ...EditableGame
                }
            }
        }
    }

    mutation DeleteMyPlayerGame($id: uuid!) {
        delete_espin_player_game_by_pk(id: $id) {
            id
        }
    }

    mutation DeleteMyPlayerGameAndChangePrimaryGame($id: uuid!, $newPrimaryId: uuid!) {
        delete_espin_player_game_by_pk(id: $id) {
            id
        }
        update_espin_player_game(where: { id: { _eq: $newPrimaryId } }, _set: { primary: true }) {
            affected_rows
        }
    }
`;

export const PlayerMyGames: React.FC<Props> = ({ user }) => {
    const { openModal, closeActiveModal } = useModals();
    const { isReady, data } = useAuthenticatedQuery<PlayerMyGamesQuery, PlayerMyGamesQueryVariables>(
        {
            query: PlayerMyGamesDocument,
            variables: useMemo(() => ({ id: user?.id }), [user]),
            pause: !!!user,
            requestPolicy: 'network-only', // always get freshest pls
        },
        ['espin_player_profile', 'espin_player_game'],
    );

    const [_, deleteSelectedGame] = useAuthenticatedMutation<
        DeleteMyPlayerGameMutation,
        DeleteMyPlayerGameMutationVariables
    >(DeleteMyPlayerGameDocument);

    const [__, deleteSelectedGameAndChangePrimaryGame] = useAuthenticatedMutation<
        DeleteMyPlayerGameAndChangePrimaryGameMutation,
        DeleteMyPlayerGameAndChangePrimaryGameMutationVariables
    >(DeleteMyPlayerGameAndChangePrimaryGameDocument);

    if (!isReady) {
        return (
            <div className="my-games">
                <Skeleton height={228} borderRadius={15} />
                <Skeleton height={228} borderRadius={15} />
            </div>
        );
    }
    const games = data.espin_user_by_pk.player_profile.player_games;

    const deleteGame = async (game: EditableGameFragment, updatePrimaryGame?: EditableGameFragment) => {
        const result = updatePrimaryGame
            ? await deleteSelectedGameAndChangePrimaryGame({ id: game.id, newPrimaryId: updatePrimaryGame.id })
            : await deleteSelectedGame({ id: game.id });
        if (result.error) {
            toast.error(`There was an error removing your game. Check details and try again`);
            console.error(result.error);
            return;
        }
        toast.success('Game removed!');
        closeActiveModal();
    };

    return (
        <div className="my-games">
            {games &&
                games.map((game, idx) => (
                    <EditableGame
                        key={idx}
                        game={game}
                        editGame={() =>
                            openModal(SpinAppModals.EDIT_GAME, {
                                game: game,
                                primaryGameId: games.filter((g) => g.primary)[0].id,
                            })
                        } // should only ever be 1 primary game
                        deleteGame={() =>
                            openModal(SpinAppModals.CONFIRMATION, {
                                popupTitle: 'Delete Game',
                                askText: (
                                    <p>
                                        Are you sure you want to delete <span>{game?.game?.name ?? ''}</span> from your
                                        games?{' '}
                                        {game?.primary && games.length > 1 && (
                                            <>
                                                Your new primary game will become{' '}
                                                <span>
                                                    {(idx < games.length - 1
                                                        ? games[idx + 1]?.game?.name
                                                        : games[idx - 1]?.game?.name) ?? ''}
                                                </span>
                                                . You can change this later.
                                            </>
                                        )}
                                    </p>
                                ),
                                cancelCTAText: 'NO',
                                confirmCTAText: 'YES',
                                cancelCTAButtonColour: 'blue',
                                confirmCTAButtonColour: 'red',
                                onConfirm: () =>
                                    deleteGame(
                                        game,
                                        game?.primary && games.length
                                            ? idx < games.length - 1
                                                ? games[idx + 1]
                                                : games[idx - 1]
                                            : null,
                                    ),
                                onCancel: () => closeActiveModal(),
                            })
                        }
                    />
                ))}
            <div className="add-game-card">
                <h2 className="card-title">Showcase your skills</h2>
                <p className="card-description">Let colleges know which games you play.</p>
                <button
                    onClick={() => openModal(SpinAppModals.ADD_GAME, { games })}
                    className="button medium-button blue-button"
                >
                    Add Game
                </button>
            </div>
        </div>
    );
};
